import { create } from 'zustand';

export type Tema = 'sistema' | 'claro' | 'escuro';
export type TemaEfetivo = 'claro' | 'escuro';

const CHAVE = 'remoto:tema';

function lido(): Tema {
  try {
    const guardado = window.localStorage.getItem(CHAVE);
    return guardado === 'claro' || guardado === 'escuro' ? guardado : 'sistema';
  } catch {
    // Modo anônimo ou cota estourada: segue no padrão do sistema.
    return 'sistema';
  }
}

function consultaDoSistema(): MediaQueryList | null {
  return typeof window === 'undefined' ? null : window.matchMedia('(prefers-color-scheme: light)');
}

function doSistema(): TemaEfetivo {
  return consultaDoSistema()?.matches ? 'claro' : 'escuro';
}

interface EstadoDoTema {
  /** O que a pessoa escolheu, inclusive "seguir o sistema". */
  escolha: Tema;
  /** O que o sistema operacional está pedindo agora. */
  sistema: TemaEfetivo;
  definir: (escolha: Tema) => void;
}

/**
 * O tema do painel.
 *
 * Na primeira visita segue o sistema — quem usa o computador no claro não
 * espera abrir uma tela preta, e vice-versa. A escolha explícita fica no
 * aparelho, junto das larguras dos painéis: é preferência de máquina.
 */
export const usarTema = create<EstadoDoTema>((set) => ({
  escolha: lido(),
  sistema: doSistema(),
  definir: (escolha) => {
    try {
      window.localStorage.setItem(CHAVE, escolha);
    } catch {
      // Sem memória a escolha vale só nesta visita, o que é melhor que travar.
    }
    set({ escolha });
  },
}));

export function temaEfetivo(estado: EstadoDoTema): TemaEfetivo {
  return estado.escolha === 'sistema' ? estado.sistema : estado.escolha;
}

/** O nome que o Monaco usa para o mesmo tema. */
export function usarTemaDoEditor(): 'vs' | 'vs-dark' {
  return usarTema(temaEfetivo) === 'claro' ? 'vs' : 'vs-dark';
}

/**
 * Liga o tema ao documento.
 *
 * Roda na carga do módulo, antes do React desenhar, para a tela não piscar
 * preta antes de ficar clara. E fica ouvindo o sistema: quem tem o computador
 * trocando de tema ao anoitecer vê o painel acompanhar sem recarregar.
 */
export function ligarTema(): void {
  const aplicar = (): void => {
    document.documentElement.dataset['tema'] = temaEfetivo(usarTema.getState());
  };

  aplicar();
  usarTema.subscribe(aplicar);

  const consulta = consultaDoSistema();
  consulta?.addEventListener('change', () => {
    usarTema.setState({ sistema: doSistema() });
  });
}
